import { map, get, uniq } from 'lodash'
import analyticsUtils from '.'

interface OccurrenceTime {
  startAt: string
  endAt: string
}

interface Occurrences {
  times: OccurrenceTime[]
}

interface Data extends MapModel {
  stores?: {
    title?: string
  }[]
  retailCategories?: {
    title?: string
  }[]
  topics?: {
    title?: string
  }[]
  occurrences?: Occurrences
  experiences?: {
    title?: string
  }[]
  typename?: string
  slug?: string
  title?: string
  retailer?: {
    title?: string
  }
  movieTitle?: string
}

interface Centre {
  country: string
  title: string
}

interface MapModel {
  __typename?: string
  path?: string
  title?: string
  typename?: string
  slug?: string
}

interface DateDataToPushDict {
  [str: string]: string | number | null
}

const arrayToString = (
  model: Record<string, any>,
  key: string,
  value: string,
  isUnique?: boolean
): string | null => {
  let dataString = null
  if (model && get(model, key)) {
    dataString = isUnique
      ? uniq(map(get(model, key), value)).join(', ')
      : map(get(model, key), value).join(', ')
  }
  return dataString
}

const getOccurences = (data: Data): Record<string, any> => {
  const dateDataToPush: DateDataToPushDict = {}
  if (
    data.typename &&
    (data.typename === 'Offer' || data.typename === 'Event')
  ) {
    const templateType = data.typename.toLowerCase()
    dateDataToPush[`${templateType}StartDate`] = data.occurrences
      ? arrayToString(data.occurrences, 'times', 'startAt')
      : null
    dateDataToPush[`${templateType}EndDate`] = data.occurrences
      ? arrayToString(data.occurrences, 'times', 'endAt')
      : null
    dateDataToPush[`${templateType}Occurrences`] = get(
      data,
      'occurrences.times.length',
      null
    )
  }
  return dateDataToPush
}

const getStoreName = (
  model: Data,
  key: string,
  value: string
): string | null => {
  if (model.typename === 'Store') {
    return get(model, 'retailer.title')
  } else {
    return arrayToString(model, key, value, true)
  }
}

const pageLoadDataLayer = (data: Data, centre?: Centre): void => {
  const dataToPush = {
    event: analyticsUtils.events.DATA_LAYER,
    storeName: getStoreName(data, 'stores', 'title'),
    retailCategory: arrayToString(data, 'retailCategories', 'title'),
    topic: arrayToString(data, 'topics', 'title'),
    templateName: data.typename ? data.typename : null,
    experiences: arrayToString(data, 'experiences', 'title'),
    country: centre && centre.country ? centre.country : null,
    centre: centre && centre.title ? centre.title : null,
    virtualPageUrl: data.slug ? data.slug : null,
    virtualPageName: data.title ? data.title : null,
    movieTitle: data.movieTitle ? data.movieTitle : null,
    ...getOccurences(data),
  }
  analyticsUtils.gaTagTracking(dataToPush)
}

export const mapTemplateToPageLoadData = (model: Data): Data => {
  const result = { ...model }
  result.typename = model.__typename
  result.slug = model.path
  return result
}

export const mapStoreToPageLoadData = (model: Data): Data => {
  const result = { ...model }
  result.typename = model.__typename
  return result
}

export const mapMovieToPageLoadData = (movie: Data, cinema: Data): Data => ({
  ...cinema,
  slug: movie.slug,
  typename: movie.typename,
  stores: [
    {
      title: get(cinema, 'title'),
    },
  ],
  title: movie.title,
  movieTitle: movie.title,
})

export const mapMovieExperienceToPageLoadData = (cinema: Data): Data => ({
  ...cinema,
  title: 'Movies',
  slug: 'movies',
  typename: cinema.__typename,
})

export default pageLoadDataLayer
